import React from 'react';
import { CheckCircle, AlertCircle, XCircle, Clock, Server, Database, Globe, Shield, Mail, MessageCircle } from 'lucide-react';

const Status: React.FC = () => {
  const systemStatus = {
    overall: "operational",
    uptime: "99.9%",
    lastIncident: "None in the last 90 days"
  };

  const services = [
    {
      name: "Website",
      status: "operational",
      uptime: "99.99%",
      responseTime: "120ms"
    },
    {
      name: "API",
      status: "operational",
      uptime: "99.95%",
      responseTime: "45ms"
    },
    {
      name: "Database",
      status: "operational",
      uptime: "99.98%",
      responseTime: "12ms"
    },
    {
      name: "AI Validation",
      status: "operational",
      uptime: "99.90%",
      responseTime: "2.3s"
    },
    {
      name: "Authentication",
      status: "operational",
      uptime: "99.99%",
      responseTime: "180ms"
    },
    {
      name: "File Storage",
      status: "operational",
      uptime: "99.97%",
      responseTime: "350ms"
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case "degraded":
        return <AlertCircle className="w-6 h-6 text-yellow-600" />;
      case "outage":
        return <XCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Clock className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "operational":
        return "text-green-600";
      case "degraded":
        return "text-yellow-600";
      case "outage":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case "operational":
        return "bg-green-50 border-green-200";
      case "degraded":
        return "bg-yellow-50 border-yellow-200";
      case "outage":
        return "bg-red-50 border-red-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-40">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              System Status
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Real-time status of all 30DayCashSprint services and infrastructure
            </p>
            
            {/* Overall Status */}
            <div className="max-w-2xl mx-auto mb-16">
              <div className={`inline-flex items-center gap-4 px-8 py-6 rounded-lg ${getStatusBg(systemStatus.overall)} border`}>
                {getStatusIcon(systemStatus.overall)}
                <div className="text-left">
                  <div className={`text-2xl font-bold ${getStatusColor(systemStatus.overall)} capitalize`}>
                    {systemStatus.overall}
                  </div>
                  <div className="text-gray-600 text-sm">
                    All systems are running normally
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{systemStatus.uptime}</div>
                <div className="text-black-400 text-sm">Uptime (30 days)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">0</div>
                <div className="text-black-400 text-sm">Active Incidents</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">90+</div>
                <div className="text-black-400 text-sm">Days Incident Free</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Status */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Service Status</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Detailed status of all our services and their performance metrics
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-6">
              {services.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(service.status)}
                    <div>
                      <h3 className="text-xl font-medium text-black">{service.name}</h3>
                      <div className="text-sm text-gray-500">
                        Uptime: {service.uptime} • Response: {service.responseTime}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status).replace('text-', 'bg-')}`}></div>
                    <span className={`font-medium capitalize ${getStatusColor(service.status)}`}>
                      {service.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Performance Metrics</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Key performance indicators and system health metrics
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Server className="w-8 h-8 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-black mb-2">99.9%</div>
              <div className="text-gray-600 text-sm">System Uptime</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Database className="w-8 h-8 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-black mb-2">12ms</div>
              <div className="text-gray-600 text-sm">Avg Response Time</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-black mb-2">15</div>
              <div className="text-gray-600 text-sm">Global Regions</div>
            </div>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
              <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-3xl font-bold text-black mb-2">0</div>
              <div className="text-gray-600 text-sm">Security Incidents</div>
            </div>
          </div>
        </div>
      </section>

      {/* Incident History */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Incident History</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Track our incident history and resolution times
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h3 className="text-2xl font-medium text-green-900 mb-4">No Recent Incidents</h3>
              <p className="text-green-800 leading-relaxed">
                We're happy to report that there have been no incidents in the last 90 days. 
                Our systems are running smoothly and efficiently.
              </p>
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-gray-600">
                For historical incident reports, please contact our support team or check our status page updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe to Updates */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8">
              Stay Updated
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-blue-200 font-light">
              Get notified about system updates, maintenance, and incidents
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <button className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                Subscribe to Status Updates
              </button>
              <button className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                View RSS Feed
              </button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Email Notifications</h3>
                <p className="text-blue-200 text-sm">Get updates via email</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Slack Integration</h3>
                <p className="text-blue-200 text-sm">Receive alerts in Slack</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-medium mb-2">Webhook Support</h3>
                <p className="text-blue-200 text-sm">Integrate with your systems</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Status;
